import React, { useCallback, useMemo, useState } from "react";
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Box,
  useColorModeValue,
  Text,
  Flex,
} from "@chakra-ui/react";
import { useNavigate, useLocation, useParams } from "react-router-dom";

const MyTabsNested = ({
  orientation = "horizontal",
  list = [],
  urlBasePath,
  includeALias = true,
  includeAgraphy = false,
  tabSegmentPosition = 2,
  subTabSegmentPosition = 3
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { alias } = useParams();

  const [tabCounts, setTabCounts] = useState({});

  const handleCountLoad = useCallback((tabId, subTabId, count) => {
    const key = `${tabId}|||${subTabId ?? ""}`;
    setTabCounts(prev => ({ ...prev, [key]: count }));
  }, []);

  // Ensure list is valid
  const safeList = useMemo(() => Array.isArray(list) ? list : [], [list]);

  // Extract tab and subTab from URL
  const pathParts = location.pathname.split("/").filter(Boolean);
  const currentTabId = pathParts[tabSegmentPosition] || (safeList[0]?.id || "");
  const currentSubTabId = pathParts[subTabSegmentPosition] || "";

  // Find the current main tab
  const currentTab = useMemo(() =>
    safeList.find(item => item.id === currentTabId) || safeList[0] || null
    , [safeList, currentTabId]);

  // Find the main tab index
  const tabIndex = useMemo(() =>
    safeList.findIndex(item => item.id === currentTabId)
    , [safeList, currentTabId]);

  // Find the current sub tab index
  const subTabIndex = useMemo(() => {
    if (!currentTab?.subTabs) return 0;
    const index = currentTab.subTabs.findIndex(subItem => subItem.id === currentSubTabId);
    return index >= 0 ? index : 0;
  }, [currentTab, currentSubTabId]);

  // Construct base path
  const getBasePath = useCallback(() => {
    let path = urlBasePath || "";
    if (includeALias && alias) path += `/${alias}`;
    if (includeAgraphy) path += "/agraphy";
    return path;
  }, [urlBasePath, includeALias, alias, includeAgraphy]);

  // Handle main tab change
  const handleTabChange = (index) => {
    const newTab = safeList[index];
    if (!newTab) return;

    const basePath = getBasePath();

    // If tab has subtabs, navigate to first subtab
    if (newTab?.subTabs && newTab?.subTabs?.length > 0) {
      const defaultSubTab = newTab?.subTabs?.[0]?.id;
      navigate(`${basePath}/${newTab?.id}/${defaultSubTab}`);
    } else {
      // If no subtabs, navigate to tab only
      navigate(`${basePath}/${newTab?.id}`);
    }
  };

  // Handle subtab change
  const handleSubTabChange = (tab, subIndex) => {
    if (!tab.subTabs || !tab.subTabs[subIndex]) return;

    const newSubTabId = tab.subTabs[subIndex].id;
    const basePath = getBasePath();

    navigate(`${basePath}/${tab.id}/${newSubTabId}`);
  };

  return (
    <Box 
      borderRadius="xl" 
      border="1px solid" 
      borderColor="gray.200" 
      bg={useColorModeValue("white", "gray.800")}
      boxShadow="sm"
      overflow="hidden"
    >
      {/* MAIN TABS */}
      <Tabs
        orientation={orientation}
        isLazy
        variant="unstyled"
        colorScheme="blue"
        index={tabIndex >= 0 ? tabIndex : 0}
        onChange={handleTabChange}
      >
        <TabList
          bg="gray.50"
          padding={orientation === "vertical" ? 4 : 0}
          display="flex"
          flexDirection={orientation === "vertical" ? "column" : "row"}
          borderBottom={orientation === "horizontal" ? "1px solid" : "none"}
          borderRight={orientation === "vertical" ? "1px solid" : "none"}
          borderColor="gray.200"
          gap={1}
          p={2}
        >
          {safeList?.map((item) => (
            <Tab
              key={item?.id || `tab-${item.title}`}
              isDisabled={item?.isDisabled}
              fontWeight="600"
              fontSize="14px"
              justifyContent="flex-start"
              width={orientation === "vertical" ? "100%" : "max-content"}
              color="gray.600"
              bg="transparent"
              borderRadius="lg"
              px={4}
              py={2.5}
              minH="44px"
              transition="all 0.2s ease"
              _hover={{
                bg: "blue.50",
                color: "blue.700",
                transform: "translateY(-1px)",
                boxShadow: "sm"
              }}
              _selected={{
                color: "white",
                bg: "linear-gradient(135deg, #4299e1 0%, #3182ce 100%)",
                boxShadow: "md",
                transform: "translateY(-1px)",
                border: "1px solid",
                borderColor: "blue.300"
              }}
              _active={{
                transform: "translateY(0px)"
              }}
              _disabled={{
                opacity: 0.5,
                cursor: "not-allowed",
                _hover: {
                  bg: "transparent",
                  transform: "none",
                  boxShadow: "none"
                }
              }}
            >
              <Text width={"max-content"}>{item?.title ?? "Unnamed Tab"}</Text>
            </Tab>
          ))}
        </TabList>

        <TabPanels>
          {safeList?.map((item) => (
            <TabPanel key={item?.id || `panel-${item.title}`} bg="white" p={0}>
              {/* Check if subTabs exist and have length > 0 */}
              {item?.subTabs && item?.subTabs?.length > 0 ? (
                /* Render SUB-TABS if they exist */
                <Box>
                  <Tabs
                    variant="unstyled"
                    colorScheme="blue"
                    index={item.id === currentTabId ? subTabIndex : 0}
                    isLazy
                    onChange={(subIndex) => handleSubTabChange(item, subIndex)}
                  >
                    <TabList
                      bg="gray.25"
                      borderBottom="1px solid"
                      borderColor="gray.200"
                      display="flex"
                      flexDirection="row"
                      gap={0.5}
                      p={3}
                    >
                      {item?.subTabs?.map((subItem) => (
                        <Tab
                          key={subItem?.id || `subtab-${subItem.title}`}
                          isDisabled={subItem?.isDisabled}
                          fontWeight="500"
                          fontSize="13px"
                          color="gray.600"
                          bg="transparent"
                          borderRadius="md"
                          px={3}
                          py={2}
                          minH="36px"
                          transition="all 0.2s ease"
                          _hover={{
                            bg: "blue.50",
                            color: "blue.600",
                            transform: "translateY(-1px)"
                          }}
                          _selected={{
                            color: "blue.700",
                            bg: "white",
                            boxShadow: "sm",
                            border: "1px solid",
                            borderColor: "blue.200",
                            transform: "translateY(-1px)"
                          }}
                          _active={{
                            transform: "translateY(0px)"
                          }}
                          _disabled={{
                            opacity: 0.5,
                            cursor: "not-allowed",
                            _hover: {
                              bg: "transparent",
                              transform: "none"
                            }
                          }}
                        >
                          <Flex alignItems="center" gap={2} width="max-content">
                            <Text width="max-content">{subItem?.title ?? "Unnamed Sub-Tab"}</Text>
                            {tabCounts[`${item.id}|||${subItem.id}`] > 0 && (
                              <Box
                                display="inline-flex"
                                alignItems="center"
                                bg="blue.50"
                                color="blue.700"
                                fontSize="xs"
                                fontWeight="700"
                                px={2}
                                py={0.5}
                                borderRadius="full"
                                border="1px solid"
                                borderColor="blue.200"
                                minW="20px"
                                justifyContent="center"
                              >
                                {tabCounts[`${item.id}|||${subItem.id}`]}
                              </Box>
                            )}
                          </Flex>
                        </Tab>
                      ))}
                    </TabList>

                    <TabPanels>
                      {item?.subTabs?.map((subItem) => (
                        <TabPanel key={subItem?.id || `subpanel-${subItem.title}`} p={6}>
                          {subItem?.Component ? (
                            <subItem.Component
                              currentTabId={currentTabId}
                              currentSubTabId={currentSubTabId}
                              alias={alias}
                              urlBasePath={urlBasePath}
                              onCountLoad={(count) => handleCountLoad(item.id, subItem.id, count)}
                            />
                          ) : (
                            <Box 
                              color="red.500" 
                              bg="red.50" 
                              p={4} 
                              borderRadius="lg" 
                              border="1px solid" 
                              borderColor="red.200"
                            >
                              Component not found
                            </Box>
                          )}
                        </TabPanel>
                      ))}
                    </TabPanels>
                  </Tabs>
                </Box>
              ) : (
                /* Render Tab.Component directly if no subTabs */
                item?.Component ? (
                  <Box p={6}>
                    <item.Component
                      currentTabId={currentTabId}
                      alias={alias}
                      urlBasePath={urlBasePath}
                      onCountLoad={(count) => handleCountLoad(item.id, null, count)}
                    />
                  </Box>
                ) : (
                  <Box 
                    color="red.500" 
                    bg="red.50" 
                    p={6} 
                    borderRadius="lg" 
                    border="1px solid" 
                    borderColor="red.200"
                  >
                    Component not found
                  </Box>
                )
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default MyTabsNested;